/**
 * AdminRoute Component
 *
 * A route wrapper that requires Administrator role.
 * Redirects unauthorized users to the home page with an error message.
 */

import { Spinner, Alert } from '@medsuite/ui-components';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { usePermissions, ADMINISTRATOR_ROLE } from '../../hooks/usePermissions';
import { useAuth } from '../../providers/AuthProvider';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute component
 * Wraps routes that require Administrator role
 */
export function AdminRoute({ children }: AdminRouteProps): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuth();
  const { isAdministrator } = usePermissions();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show access denied if not an Administrator
  if (!isAdministrator) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full mx-4">
          <Alert variant="error">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-red-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Access Denied
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You do not have permission to access the Admin section. The{' '}
                <strong>{ADMINISTRATOR_ROLE}</strong> role is required.
              </p>
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Home
              </a>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  // Render admin content
  return <>{children}</>;
}

export default AdminRoute;
